import { Controller } from "stimulus";
import Plyr from "@supercast/plyr";
import { SupercastTimeUpdateEvent, SupercastPlaybackEndedEvent } from "utils/events";
import {
  mediaErrorCategory,
  sendPlaybackBreadcrumb,
  sendPlaybackError,
  signedUrlExpiryMs
} from "utils/playback_telemetry";

// `stalled` fires whenever nothing arrives for a few seconds, which on a phone on a train is
// normal and self-healing. Capped per loaded episode so the trail stays a trail: enough to show a
// failure was preceded by a struggling connection, not enough to crowd out everything else on the
// shared breadcrumb list.
const maxStalledBreadcrumbsPerEpisode = 3;

export default class extends Controller {
  static targets = [
    "controlsTemplate",
    "playback0_9",
    "playback1",
    "playback1_1",
    "playback1_5",
    "playback2"
  ]

  static values = {
    timestamp: Number,
    markers: Array,
    itemId: String,
    itemType: String,
    mediaMetadata: Object,
    // :playback_observability. The component only renders these when the flag is on for the
    // entity, so observabilityValue defaulting to false is what keeps the un-flagged player
    // byte-for-byte what it was: no error listener, no AppSignal traffic, no breadcrumbs.
    observability: Boolean,
    surface: String,
    viewerId: String,
    subPropertyId: String,
    planTier: String,
    audioUrlExpiresAtMs: Number,
    serverNowMs: Number
  }

  connect() {
    const audio = this.element.querySelector("audio");
    const alreadyInitialized = !!audio?.plyr;

    this.initializePlayer();

    if (!alreadyInitialized) {
      this.player.on("playing", this.onTimeUpdate.bind(this));
      this.player.on("pause", this.onTimeUpdate.bind(this));
      this.player.on("ratechange", this.onRateChange.bind(this));
      this.player.on("timeupdate", this.onTimeUpdate.bind(this));
      this.player.on("ended", this.playbackEnded.bind(this));
    }

    this.setMediaSessionActionHandlers();
    this.setMediaSessionMetadata();

    if (!alreadyInitialized) {
      this.observePlayback(audio);
    }
  }

  loadEpisode({ itemId, itemType, audioUrl, timestamp, mediaMetadata }) {
    this.itemIdValue = itemId;
    this.itemTypeValue = itemType;

    // The src is swapped in here rather than at render, so this is the only moment we get to see
    // the URL that will actually be played - and the only place its expiry can be read.
    this.observeAudioUrl(audioUrl);

    if (this.player.source != audioUrl) {
      const audio = this.element.querySelector("audio");
      audio.src = audioUrl;
    }

    if (timestamp != null) {
      this.timestampValue = timestamp;
    }

    if (mediaMetadata) {
      this.mediaMetadataValue = {
        title: mediaMetadata.title,
        artist: mediaMetadata.artist,
        album: mediaMetadata.album,
        artwork: mediaMetadata.artworkUrl
          ? [{ src: mediaMetadata.artworkUrl, sizes: "512x512" }]
          : this.mediaMetadataValue.artwork
      };
      this.setMediaSessionMetadata();
    }

    this.setCurrentTime(this.element.querySelector("audio"));
    this.player.play();

    this.updateFeedTokenConnection();
  }

  updateFeedTokenConnection() {
    const episodeId = this.itemIdValue;
    if (episodeId) {
      fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
        method: "POST"
      });
    }
  }

  // Duplicated in video player - consider keeping in sync
  onRateChange() {
    const speed = this.player.speed;
    this.playback0_9Target.classList.toggle("!hidden", speed !== 0.9)
    this.playback1Target.classList.toggle("!hidden", speed !== 1)
    this.playback1_1Target.classList.toggle("!hidden", speed !== 1.1)
    this.playback1_5Target.classList.toggle("!hidden", speed !== 1.5)
    this.playback2Target.classList.toggle("!hidden", speed !== 2)
    window.dispatchEvent(new CustomEvent("supercast-rate-change", { detail: { playbackRate: speed } }));
  }

  onTimeUpdate() {
    const event = new SupercastTimeUpdateEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      currentTime: this.player.currentTime,
      duration: this.player.duration,
      isPlaying: !this.player.paused,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
    this.setMediaSessionPlaybackState();
  }

  playbackEnded() {
    const event = new SupercastPlaybackEndedEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
  }

  play() {
    console.log("Media Playback: play");
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  jumpBack() {
    this.player.currentTime -= 15;
  }

  jumpForward() {
    this.player.currentTime += 15;
  }

  toggleMute() {
    this.player.muted = !this.player.muted;
  }

  onMuteChange() {
    window.dispatchEvent(new CustomEvent("supercast-mute-change", { detail: { muted: this.player.muted } }));
  }

  seek(time) {
    this.player.currentTime = time;
  }

  // Duplicated in video player - consider keeping in sync
  togglePlaybackRate() {
    if (this.player.speed == 1) {
      this.player.speed = 1.1;
    } else if (this.player.speed == 1.1) {
      this.player.speed = 1.5;
    } else if (this.player.speed == 1.5) {
      this.player.speed = 2;
    } else if (this.player.speed == 2) {
      this.player.speed = 0.9;
    } else {
      this.player.speed = 1;
    }
  }

  initializePlayer() {
    const audio = this.element.querySelector("audio");

    if (audio.plyr) {
      this.player = audio.plyr;
      return;
    }

    if (audio.paused) {
      this.setCurrentTime(audio);
    }

    this.player = new Plyr(audio, {
      controls: this.controlsTemplateTarget.innerHTML,
      markers: this.markersValue,
      seekTime: 15,
      volume: 1
    });

    audio.addEventListener("volumechange", this.onMuteChange.bind(this));
  }

  async setCurrentTime(audio) {
    if (this.hasTimestampValue && this.timestampValue > 0) {
      audio.currentTime = this.timestampValue;
    } else if (window.playbackDb) {
      const listenData = await window.playbackDb.getPlaybackData(this.itemTypeValue, this.itemIdValue);
      if (listenData) {
        const shouldRestart = listenData.duration - listenData.currentTime < 15;
        audio.currentTime = shouldRestart ? 0 : listenData.currentTime;
      }
    }
  }

  setMediaSessionMetadata() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.mediaMetadataValue.title,
      artist: this.mediaMetadataValue.artist,
      album: this.mediaMetadataValue.album,
      artwork: this.mediaMetadataValue.artwork
    });
  }

  setMediaSessionActionHandlers() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setActionHandler("play", () => {
      this.play();
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      this.pause();
    });

    navigator.mediaSession.setActionHandler("seekbackward", () => {
      this.player.currentTime -= 15;
    });

    navigator.mediaSession.setActionHandler("seekforward", () => {
      this.player.currentTime += 15;
    });
  }

  setMediaSessionPlaybackState() {
    if (!("mediaSession" in navigator)) {
      return;
    }
    if (this.player.currentTime > this.player.duration) {
      /* We can sometimes get this when we're seeking - ignore it and wait for the next update */
      return;
    }

    navigator.mediaSession.setPositionState({
      duration: this.player.duration,
      playbackRate: this.player.speed,
      position: this.player.currentTime
    });

    navigator.mediaSession.playbackState = this.player.paused ? "paused" : "playing";
  }

  // ---------------------------------------------------------------------------------------------
  // :playback_observability
  //
  // Instrumentation only - nothing below alters what the listener hears. There is deliberately no
  // retry, no fallback source and no error UI: this player had no error handling of any kind
  // before, so the first job is to find out what actually breaks and how often, and a recovery
  // ladder built on guesses would be the change we couldn't measure.
  // ---------------------------------------------------------------------------------------------

  // Listeners go on the <audio> element rather than through Plyr. Plyr does re-broadcast `error`
  // (it is in its default `events` list), but this controller sets `audio.src` itself instead of
  // going through Plyr's source pipeline, so the element - not the Plyr instance - is the thing
  // whose failures we actually want, and `audio.error` is the authoritative MediaError either
  // way. It also means an episode still reports if the Plyr constructor ever fails.
  observePlayback(audio) {
    if (!this.observabilityValue || !audio) return;

    try {
      const state = this.playbackState();
      // Snapshotted here and not recomputed later: serverNowMs is a render-time reading, so the
      // difference is only skew at the moment of connect.
      state.clockSkewMs = this.hasServerNowMsValue ? this.serverNowMsValue - Date.now() : 0;
      state.tokenExpiresAtMs = this.hasAudioUrlExpiresAtMsValue && this.audioUrlExpiresAtMsValue > 0
        ? this.audioUrlExpiresAtMsValue
        : null;

      audio.addEventListener("error", this.onMediaError.bind(this));
      audio.addEventListener("stalled", this.onMediaStalled.bind(this));
    } catch {
      // A player that reports nothing is strictly better than a player that doesn't play.
    }
  }

  // State lives on the element, not on the controller instance, because the fixed player is
  // data-turbo-permanent: the <audio> node survives every navigation while Stimulus tears down and
  // rebuilds the controller around it. The error listeners are bound to whichever instance was
  // alive when the element was created, so anything they read has to outlive that instance.
  // (Stimulus values are already element-backed, which is why they need no special handling.)
  playbackState() {
    const audio = this.element.querySelector("audio");
    if (!audio) return {};

    if (!audio.supercastPlaybackState) {
      audio.supercastPlaybackState = { clockSkewMs: 0, tokenExpiresAtMs: null, tokenLifetimeMs: null, stalls: 0 };
    }

    return audio.supercastPlaybackState;
  }

  // Called from loadEpisode with the URL about to be played. On the fixed player this is the only
  // source of an expiry at all: the component renders no src, because no episode has been chosen
  // when the persistent player first appears.
  observeAudioUrl(audioUrl) {
    if (!this.observabilityValue) return;

    try {
      const state = this.playbackState();
      state.tokenExpiresAtMs = signedUrlExpiryMs(audioUrl);
      state.tokenLifetimeMs = state.tokenExpiresAtMs === null ? null : state.tokenExpiresAtMs - this.now();
      state.stalls = 0;
    } catch {
      // Leave the previous episode's expiry in place rather than asserting a wrong one.
    }
  }

  // The report. One per `error` event - the element fires it once per failed load, so this is not
  // a firehose even on a flapping connection (that shows up as `stalled` breadcrumbs instead).
  onMediaError() {
    const audio = this.element.querySelector("audio");
    const mediaError = audio && audio.error;
    const code = mediaError && mediaError.code;

    this.reportPlaybackError("Audio playback failed", {
      media_error_code: code,
      media_error_category: mediaErrorCategory(code),
      // Browsers put a vendor-specific explanation here and it is frequently empty; kept because
      // when it is populated it is the difference between "network" and "which network".
      media_error_message: mediaError && mediaError.message,
      network_state: audio && audio.networkState,
      ready_state: audio && audio.readyState,
      // Whether the failure happened at the start or two thirds of the way through separates a
      // broken asset from an expiring credential.
      position_seconds: audio && Number.isFinite(audio.currentTime) ? Math.round(audio.currentTime) : null,
      stalls: this.playbackState().stalls
    });
  }

  onMediaStalled() {
    if (!this.observabilityValue) return;

    const state = this.playbackState();
    state.stalls = (state.stalls || 0) + 1;
    if (state.stalls > maxStalledBreadcrumbsPerEpisode) return;

    const audio = this.element.querySelector("audio");

    this.addPlaybackBreadcrumb("stalled", {
      attempt: state.stalls,
      ready_state: audio && audio.readyState,
      position_seconds: audio && Number.isFinite(audio.currentTime) ? Math.round(audio.currentTime) : null,
      ...this.tokenExpiryTags()
    });
  }

  // The single most valuable thing on an audio report. Audio URLs are CDN-signed for ~6 hours, and
  // until now an expired credential and a dropped connection were the same silent failure: both
  // surface as MEDIA_ERR_NETWORK with nothing to tell them apart. This says which.
  //
  //   token_expiry        "expired" | "valid" | "unknown"
  //   token_expires_in_s  seconds left at the moment of the error; negative once expired
  //   token_lifetime_s    how long the credential was good for when playback started
  //
  // "unknown" is a real, common and honest third answer, not a failure to compute: the subscriber
  // player is often handed a Rails redirect route whose signed target the browser never shows us,
  // and the embed plays a feeds-service URL authorized by a feed token that has no expiry at all.
  // Defaulting it to "expired" would invent an auth incident out of every one of those.
  tokenExpiryTags() {
    const state = this.playbackState();

    if (state.tokenExpiresAtMs === null || state.tokenExpiresAtMs === undefined) {
      return { token_expiry: "unknown" };
    }

    const remainingSeconds = Math.round((state.tokenExpiresAtMs - this.now()) / 1000);

    return {
      token_expiry: remainingSeconds <= 0 ? "expired" : "valid",
      token_expires_in_s: remainingSeconds,
      token_lifetime_s: state.tokenLifetimeMs === null || state.tokenLifetimeMs === undefined
        ? null
        : Math.round(state.tokenLifetimeMs / 1000)
    };
  }

  // Server time. A device clock that is days out would otherwise report every token as expired -
  // or, worse, hide a real expiry - and a wrong clock is exactly the sort of thing a listener with
  // playback problems turns out to have. Falls back to the device clock when the surface rendered
  // no server reference.
  now() {
    return Date.now() + (this.playbackState().clockSkewMs || 0);
  }

  // Context attached to every report, mirroring the video player's so the two can be sliced the
  // same way and joined through viewer_id / sub_property_id.
  playbackTags() {
    return {
      media_type: "audio",
      surface: this.surfaceValue,
      plan_tier: this.planTierValue,
      sub_property_id: this.subPropertyIdValue,
      viewer_id: this.viewerIdValue,
      item_id: this.itemIdValue,
      item_type: this.itemTypeValue,
      // Should always be "web": the native apps render a bridge--playback-listener instead of this
      // player and hide the web one. Recorded anyway so the claim is checkable rather than assumed.
      client_surface: document.querySelector("[data-hotwire-native]") ? "native" : "web",
      ...this.tokenExpiryTags()
    };
  }

  addPlaybackBreadcrumb(action, metadata = {}) {
    if (!this.observabilityValue) return;

    sendPlaybackBreadcrumb("audio_player", action, {
      ...metadata,
      item_id: this.itemIdValue,
      surface: this.surfaceValue
    });
  }

  reportPlaybackError(message, tags = {}) {
    if (!this.observabilityValue) return undefined;

    return sendPlaybackError(message, {
      action: "media--audio-player",
      tags: { ...this.playbackTags(), ...tags }
    });
  }
}
